import cn from 'classnames'
import { useMemo } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { Loader } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IAttentionReminderList, IAttentionReminderRoute } from '../../../../lib'
import { useAttentionReminder } from '../../hooks/useAttentionReminder'
import { useAttentionReminderScope } from '../../hooks/useAttentionReminderScope'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import type { IAttentionStatusesReminder } from '../../types'

import styles from './AttentionReminderButton.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.reminder

interface IAttentionReminderButtonProps {
  reminder: IAttentionStatusesReminder
  /** Who the reminder reaches and the status it is about; a `null` status sends the summary. */
  route: IAttentionReminderRoute
  /** The partners one mail lists; without it the mail is about the partner the reminder carries. */
  list?: IAttentionReminderList
  /** Fills the bell's box in the accent, where the reminder is the row's main act. */
  filled?: boolean
  className?: string
}

/**
 * Reminds one PBM or manager without opening the row. The service says whether they can be reminded
 * now: a person reminded today reads so, and one the service may not write to cannot be clicked.
 */
export const AttentionReminderButton = ({
  reminder,
  route,
  list,
  filled = false,
  className,
}: IAttentionReminderButtonProps) => {
  const t = useSafetyTranslation()
  const text = useAttentionStatusesText()
  const scope = useAttentionReminderScope()
  const recipients = useMemo(() => [reminder], [reminder])
  const { remind, stateOf, isChecking } = useAttentionReminder({ recipients, route, scope, list })
  const state = stateOf(reminder.email)
  const isSent = state === 'remindedToday'
  const isBusy = isChecking || state === 'pending'
  const label = isSent
    ? text.remindedLabel(reminder.name)
    : state === 'blocked'
      ? t(KEYS.blockedNoRecipient)
      : text.remindLabel(reminder.name)

  const button = (
    <button
      type="button"
      className={cn(styles.root, { [styles.sent]: isSent, [styles.filled]: filled }, className)}
      aria-label={label}
      disabled={isBusy || isSent || state === 'blocked'}
      onClick={(event) => {
        event.stopPropagation()
        void remind()
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {/* From the click until the service says where the person stands, the box waits rather
        than showing a bell it may take back. */}
      {isBusy ? (
        <Loader className={styles.loader} dimension={{ width: 16, height: 16 }} />
      ) : (
        <i className={cn('kbase', isSent ? 'kbase-check' : 'kbase-bell')} aria-hidden="true" />
      )}
    </button>
  )

  return (
    <Tooltip inline tipContent={() => <span>{label}</span>}>
      {button}
    </Tooltip>
  )
}

AttentionReminderButton.displayName = 'AttentionReminderButton'
